'use client';

import { ActivityTimeline } from '@/components/chat/ActivityTimeline';
import { ChatLiveStatus } from '@/components/chat/ChatLiveStatus';
import type { ThreadItem, TurnItem, TurnSegment } from '@/components/chat/thread-model';
import { MarkdownBody } from '@/components/common/MarkdownBody';
import { usePreference } from '@/domains/common/user-preferences';
import type { ChatAction } from '@/domains/matilda-agent/chat';
import { useState, type FunctionComponent } from 'react';

type Variant = 'stage' | 'floating';

const VARIANT_CLASSES: Record<Variant, { agentCard: string; userCard: string }> = {
  stage: {
    agentCard: 'rounded-[3px_12px_12px_12px]',
    userCard: 'rounded-[12px_12px_3px_12px] bg-ink text-white',
  },
  floating: {
    agentCard: 'rounded-[8px]',
    userCard: 'rounded-[8px] border border-acc-soft bg-acc-soft text-ink',
  },
};

type ChipPhase = 'idle' | 'busy' | 'done' | 'error';

interface ThreadViewProps {
  items: ThreadItem[];
  variant: Variant;
  streaming: boolean;
  onConfirm: (turnId: string, segmentId: string, recommendation: 'confirm' | 'reject') => void;
  onChipAction?: (action: ChatAction) => Promise<void>;
}

export const ThreadView: FunctionComponent<ThreadViewProps> = ({
  items,
  variant,
  streaming,
  onConfirm,
  onChipAction,
}) => {
  const [showDetails] = usePreference('show_tool_logs');
  const [chipPhases, setChipPhases] = useState<Record<string, ChipPhase>>({});

  async function runChip(key: string, action: ChatAction): Promise<void> {
    if (!onChipAction) return;
    setChipPhases(cur => ({ ...cur, [key]: 'busy' }));
    await onChipAction(action);
    setChipPhases(cur => ({ ...cur, [key]: 'done' }));
  }

  return (
    <>
      {items.map(item => {
        if (item.kind === 'user') {
          return (
            <div className="flex flex-col gap-1.25 max-w-full items-end" key={item.id}>
              <div className="text-[10.5px] text-ink-4 flex gap-1.25 pr-1 font-sans">
                {item.queued ? 'queued' : item.time}
              </div>
              <div
                className={`max-w-[85%] px-3 py-2.25 text-[12.5px] leading-[1.55] ${VARIANT_CLASSES[variant].userCard} ${item.queued ? 'opacity-60' : ''}`}
              >
                {item.body}
              </div>
            </div>
          );
        }

        if (item.kind === 'note') {
          return (
            <div className="flex flex-col gap-1.25 max-w-full" key={item.id}>
              <div className="text-[10.5px] text-ink-4 flex gap-1.25 pl-0.5 font-sans">
                <span className="text-acc font-medium">{item.agent}</span>
              </div>
              <div
                className={`border border-line bg-bg-2 px-3 py-2.25 text-ink-2 text-[12.5px] leading-[1.55] ${VARIANT_CLASSES[variant].agentCard}`}
              >
                {item.body}
              </div>
            </div>
          );
        }

        return (
          <div className="flex flex-col gap-1.25 max-w-full" key={item.id}>
            <div className="text-[10.5px] text-ink-4 flex gap-1.25 pl-0.5 font-sans">
              <span className="text-acc font-medium">{item.agent}</span>
              {variant === 'stage' ? <span>· {item.time}</span> : null}
            </div>
            <div
              className={`flex flex-col gap-2 border border-line bg-bg-2 px-3 py-2.25 text-ink-2 text-[12.5px] leading-[1.55] ${VARIANT_CLASSES[variant].agentCard}`}
            >
              {item.segments.map(segment => (
                <Segment
                  key={segment.id}
                  turn={item}
                  segment={segment}
                  streaming={streaming}
                  showDetails={showDetails}
                  onConfirm={onConfirm}
                />
              ))}
              {item.attach ? (
                <div className="bg-bg border border-line rounded-[6px] px-3 py-2 text-[11.5px] font-variant-numeric-tabular-nums">
                  <div className="text-[10.5px] text-ink-3 mb-1 font-medium font-sans">
                    {item.attach.header}
                  </div>
                  <table className="w-full border-collapse text-[11.5px]">
                    <tbody>
                      {item.attach.rows.map(([k, v], j) => (
                        <tr key={j}>
                          <td>{k}</td>
                          <td>{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
              {item.chips.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {item.chips.map((action, j) => {
                    const key = `${item.id}:${j}`;
                    const phase = chipPhases[key] ?? 'idle';
                    const disabled = phase === 'busy' || phase === 'done';
                    const label =
                      phase === 'busy' ? '…' : phase === 'done' ? doneLabel(action) : chipLabel(action);
                    return (
                      <button
                        key={j}
                        className={`px-2.25 py-1 text-[11.5px] rounded-[5px] border disabled:opacity-85 transition-all font-sans cursor-pointer ${
                          'primary' in action && action.primary
                            ? 'border-acc bg-acc text-white hover:bg-acc-2'
                            : phase === 'done'
                              ? 'border-green-soft bg-green-soft text-green'
                              : 'border-line-2 bg-bg text-ink-2 hover:bg-bg-hover hover:text-ink'
                        }`}
                        disabled={disabled}
                        onClick={() => void runChip(key, action)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {item.streaming && item.liveStatus ? (
                <ChatLiveStatus status={item.liveStatus} />
              ) : item.streaming && showTypingDots(item) ? (
                <TypingDots />
              ) : null}
              {item.stopped ? <div className="text-[10.5px] text-ink-4 italic">Stopped</div> : null}
            </div>
          </div>
        );
      })}
    </>
  );
};

function showTypingDots(turn: TurnItem): boolean {
  const last = turn.segments[turn.segments.length - 1];
  return !last || last.kind !== 'text';
}

interface SegmentProps {
  turn: TurnItem;
  segment: TurnSegment;
  streaming: boolean;
  showDetails: boolean;
  onConfirm: ThreadViewProps['onConfirm'];
}

const Segment: FunctionComponent<SegmentProps> = ({ turn, segment, streaming, showDetails, onConfirm }) => {
  if (segment.kind === 'text') {
    return <MarkdownBody>{segment.body}</MarkdownBody>;
  }

  if (segment.kind === 'activity') {
    return <ActivityTimeline rows={segment.rows} showDetails={showDetails} />;
  }

  const disabled = streaming || segment.state === 'busy' || segment.state === 'done';
  return (
    <div
      className={`flex flex-col gap-1.5 rounded-[7px] border px-3 py-2.5 ${
        segment.state === 'done'
          ? 'border-green-soft bg-[color-mix(in_oklab,var(--green-soft)_30%,var(--bg-2))]'
          : segment.state === 'error'
            ? 'border-rose-soft bg-[color-mix(in_oklab,var(--rose-soft)_30%,var(--bg-2))]'
            : 'border-line-2 bg-bg'
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-[11px] text-ink-2 bg-bg-2 px-1.5 py-0.25 rounded border border-line">
          {segment.tool_name}
        </span>
      </div>
      <div className="text-[12.5px] text-ink leading-[1.45]">{segment.summary}</div>
      <div className="flex gap-1.5">
        <button
          className="px-2.25 py-1 text-[11.5px] rounded-[5px] border border-acc bg-acc text-white disabled:opacity-85 transition-all font-sans cursor-pointer"
          disabled={disabled}
          onClick={() => onConfirm(turn.id, segment.id, 'confirm')}
        >
          {segment.state === 'busy' ? '…' : segment.state === 'done' ? 'Confirmed ✓' : 'Confirm'}
        </button>
        <button
          className="px-2.25 py-1 text-[11.5px] rounded-[5px] border border-line-2 bg-bg-2 text-ink-2 disabled:opacity-85 transition-all font-sans cursor-pointer hover:bg-bg-hover hover:text-ink"
          disabled={disabled}
          onClick={() => onConfirm(turn.id, segment.id, 'reject')}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

const TypingDots: FunctionComponent = () => (
  <div className="flex gap-[3px] py-0.5" aria-label="Matilda is thinking">
    <span className="h-1 w-1 animate-[onb-typing_1.4s_infinite] rounded-full bg-ink-3" />
    <span className="h-1 w-1 animate-[onb-typing_1.4s_infinite] rounded-full bg-ink-3 [animation-delay:0.15s]" />
    <span className="h-1 w-1 animate-[onb-typing_1.4s_infinite] rounded-full bg-ink-3 [animation-delay:0.3s]" />
  </div>
);

function chipLabel(action: ChatAction): string {
  return 'label' in action ? action.label : '';
}

function doneLabel(action: ChatAction): string {
  switch (action.kind) {
    case 'resolve_exception':
      return 'Resolved ✓';
    case 'hold_exception':
      return 'On hold ✓';
    case 'send_email':
      return 'Sent ✓';
    case 'open_draft':
      return 'Opened ✓';
    case 'show_affected':
      return 'Shown ✓';
    case 'open_source':
      return 'Opened ✓';
    case 'open_stage':
      return 'Opened ✓';
    case 'noop':
      return `${action.label} ✓`;
    case 'tool_call':
      return 'Confirmed ✓';
    case 'tool_activity':
      return '';
  }
}
